import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import { WarehouseDto } from "@insite/client-framework/Types/ApiModels";
import { DataTableRowProps } from "@insite/mobius/DataTable/DataTableRow";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import Icon, { IconProps } from "@insite/mobius/Icon";
import Check from "@insite/mobius/Icons/Check";
import XIcon from "@insite/mobius/Icons/X";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import getContrastColor from "@insite/mobius/utilities/getContrastColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import GridItem, { GridItemProps } from "@store/MobiusOverrides/GridItem/GridItem";
import { tableGridProps } from "@store/theme/common/props/tableGrid";
import { TYPOGRAPHY } from "@store/theme/design";
import getBooleanProperty from "@store/utilities/getBooleanProperty";
import React, { FC } from "react";
import { css } from "styled-components";

interface OwnProps {
    warehouses?: WarehouseDto[];
    currentWarehouseName?: string;
    qtyOnHand?: number;
    isHighDemandMode?: boolean;
    showGlobalQuantityInventory?: boolean;
}

type Props = OwnProps;

export interface ProductAvailabilityStyles {
    availabilityByWarehouseTableRowActive?: DataTableRowProps;
    currentWarehouseIsStocked?: TypographyPresentationProps;
    warehouseIsStocked?: TypographyPresentationProps;
    warehouseIsNotStocked?: TypographyPresentationProps;
    warehouseIsStockedIcon?: IconProps;
    warehouseIsNotStockedIcon?: IconProps;
    container?: InjectableCss;
    wrapper?: InjectableCss;
    tableGridHead?: GridContainerProps;
    quantityTableGridHeader?: GridItemProps;
    tableGridBody?: GridContainerProps;
    tableGridRow?: GridItemProps;
    tableGridRowContainer?: GridContainerProps;
    availabilityTableGridCell?: GridItemProps;
    branchTableGridHeader: GridItemProps;
    warehouseIsStockedTableGridHeader: GridItemProps;
    branchTableGridCell: GridItemProps;
    warehouseIsStockedTableGridCell: GridItemProps;
}

const warehouseLineTypographyProps = (color: string) => {
    return {
        color,
        weight: TYPOGRAPHY.weight.bold,
        css: css`
            display: flex;
            align-items: center;
            justify-content: center;
        `,
    };
};

export const productAvailabilityStyles: ProductAvailabilityStyles = {
    container: {
        ...tableGridProps.container,
        css: css `
        margin-bottom: 5px;
        `,
    },
    tableGridHead: tableGridProps.tableGridHead,
    tableGridRowContainer: tableGridProps.tableGridRowContainer,
    tableGridBody: {
        gap: 0,
        css: css`
            text-align: center
            font-size: 14px;
        `,
    },
    tableGridRow: {
        ...tableGridProps.tableGridRow,
        css: css`
            font-size: 14px;
            &:nth-child(even) {
                background-color: ${getColor("common.accent")}
            }
        `,
    },
    quantityTableGridHeader: {
        ...tableGridProps.tableGridHeader,
        width: 2,
        css: css`
            background-color: ${getColor("common.border")};
            color: ${getColor("text")};
            font-weight: ${TYPOGRAPHY.weight.bold};
            text-align: center;
            justify-content: center;
        `,
    },
    warehouseIsStockedTableGridHeader: {
        ...tableGridProps.tableGridHeader,
        width: [5, 5, 4, 4, 4],
        css: css`
            background-color: ${getColor("common.border")};
            color: ${getColor("text")};
            font-weight: ${TYPOGRAPHY.weight.bold};
            text-align: center;
            justify-content: center;
        `,
    },
    branchTableGridHeader: {
        ...tableGridProps.tableGridHeader,
        width: [5, 5, 6, 6, 6],
        css: css`
            background-color: ${getColor("common.border")};
            color: ${getColor("text")};
            font-weight: ${TYPOGRAPHY.weight.bold};
        `,
    },
    branchTableGridCell: {
        ...tableGridProps.tableGridCell,
        width: [5, 5, 6, 6, 6],
    },
    availabilityTableGridCell: {
        ...tableGridProps.tableGridCell,
        width: 2,
        css: css`
            text-align: center;
            justify-content: center;
        `,
    },
    warehouseIsStockedTableGridCell: {
        ...tableGridProps.tableGridCell,
        width: [5, 5, 4, 4, 4],
        css: css`
            text-align: center;
            justify-content: center;
        `,
    },
    availabilityByWarehouseTableRowActive: {
        ...tableGridProps.tableGridRow,
        css: css`
            font-size: 14px;
            border-bottom: 1px solid ${getColor("common.border")};
            background-color: ${getColor("primary.light")};
            color: ${getColor("primary.contrast")};
            font-weight: ${TYPOGRAPHY.weight.bold};
        `,
    },
    currentWarehouseIsStocked: warehouseLineTypographyProps("common.background"),
    warehouseIsStocked: warehouseLineTypographyProps("success"),
    warehouseIsNotStocked: warehouseLineTypographyProps("danger"),
    warehouseIsStockedIcon: {
        src: Check,
        css: css`
            margin-right: 0.3em;
        `,
    },
    warehouseIsNotStockedIcon: {
        src: XIcon,
        css: css`
            margin-right: 0.3em;
        `,
    },
};

const styles = productAvailabilityStyles;

const StoreAvailabilityTable: FC<Props> = ({ warehouses, currentWarehouseName, qtyOnHand, isHighDemandMode, showGlobalQuantityInventory }) => {
    const hasStocked = warehouses?.some(x => (x as any).properties?.isStocked);

    const yes = (isCurrentWarehouse: boolean) =>
        <Typography {...(isCurrentWarehouse ? styles.currentWarehouseIsStocked : styles.warehouseIsStocked)}>
            <Icon {...styles.warehouseIsStockedIcon} />{translate("Yes")}
        </Typography>;
    const no = (isCurrentWarehouse: boolean) =>
        <Typography {...(isCurrentWarehouse ? styles.currentWarehouseIsStocked : styles.warehouseIsNotStocked)}>
            <Icon {...styles.warehouseIsNotStockedIcon} />{translate("No")}
        </Typography>;

    return (
        <StyledWrapper {...styles.wrapper} data-test-selector="availabilityByWarehouseModal" >
            <StyledWrapper {...styles.container}>
                <GridContainer {...styles.tableGridHead}>
                    <GridItem {...styles.branchTableGridHeader}>
                        {translate("Branch")}
                    </GridItem>
                    <GridItem {...styles.quantityTableGridHeader}>
                        {isHighDemandMode ? translate("Available") : translate("QTY")}
                    </GridItem>
                    {hasStocked && (
                        <GridItem {...styles.warehouseIsStockedTableGridHeader}>
                            {translate("Stocking Item")}
                        </GridItem>
                    )}
                </GridContainer>
                <GridContainer
                    {...styles.tableGridBody}>
                    {warehouses?.map(warehouse => {
                        const isCurrentWarehouse = warehouse.name === currentWarehouseName;
                        const rowStyles = isCurrentWarehouse
                            ? styles.availabilityByWarehouseTableRowActive
                            : styles.tableGridRow;
                        const isStockedProperty = (warehouse as any).properties?.isStocked;
                        const stockedMessage = isStockedProperty ? (
                            getBooleanProperty(isStockedProperty) ? (
                                yes(isCurrentWarehouse)
                            ) : (
                                no(isCurrentWarehouse)
                            )
                        ) : null;
                        return (
                            <GridItem {...styles.tableGridRow}{...rowStyles} key={warehouse.name}>
                                <GridContainer
                                    {...styles.tableGridRowContainer}>
                                    <GridItem {...styles.branchTableGridCell}>
                                        {warehouse.name} - {warehouse.description}
                                    </GridItem>
                                    <GridItem {...styles.availabilityTableGridCell}>
                                        {isHighDemandMode
                                            ? warehouse.qty > 0
                                                ? (yes(isCurrentWarehouse))
                                                : (no(isCurrentWarehouse))
                                            : (warehouse.qty)}
                                    </GridItem>
                                    {hasStocked && (
                                        <GridItem {...styles.warehouseIsStockedTableGridCell}>
                                            {stockedMessage}
                                        </GridItem>
                                    )}
                                </GridContainer>
                            </GridItem>
                        );
                    })}
                </GridContainer>
                <GridContainer {...styles.tableGridHead}>
                    <GridItem {...styles.branchTableGridHeader}>
                        {translate("Global")}
                    </GridItem>
                    <GridItem {...styles.quantityTableGridHeader}>
                        {isHighDemandMode ? qtyOnHand && qtyOnHand > 0 ? (yes(false)) : (no(false)) : (qtyOnHand)}
                    </GridItem>
                    {hasStocked && (
                        <GridItem {...styles.warehouseIsStockedTableGridHeader}></GridItem>
                    )}
                </GridContainer>
            </StyledWrapper>
        </StyledWrapper>
    );
};

export default StoreAvailabilityTable;
